import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, Check, WarningCircle } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_BASE}/api/contact`;

const DEPARTMENTS = [
  { value: "sales", label: "Sales — new partner inquiry" },
  { value: "media-buying", label: "Media buying" },
  { value: "compliance", label: "Compliance" },
  { value: "finance", label: "Finance / Accounting" },
  { value: "press", label: "Press & media" },
  { value: "support", label: "Partner support" },
  { value: "other", label: "Other" },
];

const initialForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  department: "",
  message: "",
  website: "", // honeypot
};

const labelClass =
  "block font-body text-[11px] uppercase tracking-[0.2em] text-citirex-sub";
const inputClass =
  "mt-2 block w-full border border-citirex-border bg-citirex-bg px-4 py-3 font-body text-sm text-citirex-ink outline-none transition-colors focus:border-citirex-ink";

export default function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", message: "" });
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const update = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = "Required";
    if (!form.last_name.trim()) next.last_name = "Required";
    if (!form.phone.trim() || form.phone.trim().length < 7)
      next.phone = "Enter a valid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Enter a valid email";
    if (!form.department) next.department = "Pick one";
    if (!form.message.trim() || form.message.trim().length < 10)
      next.message = "At least 10 characters";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state.status === "submitting") return;
    if (!validate()) return;

    setState({ status: "submitting", message: "" });
    try {
      const elapsed_ms = Date.now() - mountedAt.current;
      await axios.post(ENDPOINT, { ...form, elapsed_ms });
      setState({
        status: "success",
        message:
          "Thanks — your inquiry is in. The Citirex partnerships desk will reply within one business day.",
      });
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong sending your message. Please try again in a moment.";
      setState({ status: "error", message: detail });
    }
  };

  if (state.status === "success") {
    return (
      <div
        data-testid="contact-form-success"
        className="flex items-start gap-4 border border-citirex-border bg-citirex-surface p-8"
      >
        <Check
          size={32}
          weight="bold"
          className="mt-1 shrink-0 text-citirex-accent"
        />
        <div>
          <p className="overline">Received</p>
          <h3 className="mt-2 font-display text-2xl font-medium text-citirex-ink md:text-3xl">
            We've got it.
          </h3>
          <p className="mt-3 font-body text-base text-citirex-sub">
            {state.message}
          </p>
          <button
            type="button"
            onClick={() => setState({ status: "idle", message: "" })}
            data-testid="contact-form-reset"
            className="mt-6 font-body text-xs font-semibold uppercase tracking-[0.2em] text-citirex-accent hover:underline"
          >
            Send another →
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      noValidate
      className="border border-citirex-border bg-citirex-surface p-8 md:p-10"
    >
      {/* Honeypot — hidden from humans, irresistible to bots */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: "auto",
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <label htmlFor="contact-website">
          Website (leave blank)
          <input
            id="contact-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-first-name" className={labelClass}>
            First name
          </label>
          <input
            id="contact-first-name"
            data-testid="contact-input-first-name"
            type="text"
            required
            autoComplete="given-name"
            value={form.first_name}
            onChange={update("first_name")}
            className={inputClass}
          />
          {errors.first_name && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.first_name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="contact-last-name" className={labelClass}>
            Last name
          </label>
          <input
            id="contact-last-name"
            data-testid="contact-input-last-name"
            type="text"
            required
            autoComplete="family-name"
            value={form.last_name}
            onChange={update("last_name")}
            className={inputClass}
          />
          {errors.last_name && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.last_name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="contact-phone" className={labelClass}>
            Phone
          </label>
          <input
            id="contact-phone"
            data-testid="contact-input-phone"
            type="tel"
            required
            autoComplete="tel"
            value={form.phone}
            onChange={update("phone")}
            className={inputClass}
          />
          {errors.phone && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.phone}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="contact-email" className={labelClass}>
            Work email
          </label>
          <input
            id="contact-email"
            data-testid="contact-input-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={update("email")}
            className={inputClass}
          />
          {errors.email && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.email}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="contact-department">
            Department
          </label>
          <Select
            value={form.department}
            onValueChange={(v) => update("department")(v)}
          >
            <SelectTrigger
              id="contact-department"
              data-testid="contact-input-department"
              className="mt-2 h-auto w-full rounded-none border border-citirex-border bg-citirex-bg px-4 py-3 font-body text-sm text-citirex-ink shadow-none focus:border-citirex-ink focus:ring-0 focus:ring-offset-0"
            >
              <SelectValue placeholder="Select a department" />
            </SelectTrigger>
            <SelectContent>
              {DEPARTMENTS.map((d) => (
                <SelectItem
                  key={d.value}
                  value={d.value}
                  data-testid={`contact-department-option-${d.value}`}
                  className="font-body text-sm"
                >
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.department && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.department}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="contact-message" className={labelClass}>
            Message
          </label>
          <textarea
            id="contact-message"
            data-testid="contact-input-message"
            required
            rows={6}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us about your appetite, volume targets and the vertical you're focused on."
            className={`${inputClass} resize-y`}
          />
          {errors.message && (
            <p className="mt-1 font-body text-xs text-citirex-accent">
              {errors.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-col items-start gap-4 border-t border-citirex-border pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-sm font-body text-xs text-citirex-sub">
          By submitting, you agree to be contacted by Citirex about your
          inquiry. We never share your details with third parties.
        </p>
        <button
          type="submit"
          data-testid="contact-submit"
          disabled={state.status === "submitting"}
          className="group inline-flex items-center gap-2 bg-citirex-ink px-7 py-4 font-body text-sm font-semibold uppercase tracking-wider text-citirex-inv transition-colors hover:bg-citirex-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {state.status === "submitting" ? "Sending..." : "Send message"}
          <ArrowUpRight
            size={16}
            weight="bold"
            className="transition-transform group-hover:rotate-45"
          />
        </button>
      </div>

      {state.status === "error" && (
        <div
          data-testid="contact-form-error"
          role="alert"
          className="mt-6 flex items-start gap-3 border border-citirex-accent/40 bg-citirex-accent/5 p-4"
        >
          <WarningCircle
            size={20}
            weight="bold"
            className="mt-0.5 shrink-0 text-citirex-accent"
          />
          <p className="font-body text-sm text-citirex-ink">{state.message}</p>
        </div>
      )}
    </form>
  );
}
